import { useState } from "react";
import axios from "axios";
import { Twitter, Send, MessageCircle, Instagram, Globe, MailCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { API, SITE } from "@/config/site";
import { SectionHead, Reveal } from "@/components/common";
import { toast } from "sonner";

const SOCIALS = [
  { key: "x", label: "X / Twitter", Icon: Twitter },
  { key: "telegram", label: "Telegram", Icon: Send },
  { key: "discord", label: "Discord", Icon: MessageCircle },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "site", label: "Website", Icon: Globe },
];

export function Community() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error(t("comm.consent"));
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, consent });
      toast.success(t("comm.subscribed"));
      setEmail("");
      setConsent(false);
    } catch {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section-pad py-24" data-testid="community-section">
      <SectionHead eyebrow={t("comm.eyebrow")} title={t("comm.title")} sub={t("comm.sub")} />
      <div className="mt-12 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid gap-4 sm:grid-cols-2">
          {SOCIALS.map((s, i) => {
            const href = s.key === "site" ? "/" : SITE.socials[s.key];
            const inner = (
              <>
                <s.Icon size={22} className={href ? "text-neon" : "text-mist/50"} />
                <span className="font-heading text-sm font-bold uppercase tracking-wider">{s.label}</span>
                {!href && <span className="mono-chip !py-0.5 text-[9px]">{t("common.comingSoon")}</span>}
              </>
            );
            const cls = `card-surface flex items-center gap-3 p-5 ${href ? "text-paper" : "cursor-default text-mist/60"}`;
            return (
              <Reveal key={s.key} delay={i * 0.05}>
                {href ? (
                  s.key === "site"
                    ? <a href="/" className={cls} data-testid={`comm-${s.key}`}>{inner}</a>
                    : <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`comm-${s.key}`}>{inner}</a>
                ) : (
                  <div className={cls} data-testid={`comm-${s.key}`}>{inner}</div>
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={subscribe} className="card-surface h-full p-7" data-testid="newsletter-form">
            <div className="flex items-center gap-3">
              <MailCheck size={20} className="text-goldlight" />
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-paper">{t("comm.newsTitle")}</h3>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-mist">{t("comm.newsSub")}</p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("comm.email")}
              data-testid="newsletter-email"
              className="mt-5 w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-mist/50 focus:border-neon/60 focus:outline-none"
            />
            <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-xs text-mist">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="newsletter-consent"
                className="mt-0.5 h-4 w-4 accent-[#A8FF3E]"
              />
              {t("comm.consent")}
            </label>
            <button type="submit" disabled={busy} className="btn-neon mt-5 w-full disabled:opacity-50" data-testid="newsletter-submit">
              {t("comm.subscribe")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
